import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Share, X, Download } from 'lucide-react';

const DISMISS_KEY = 'underline.install.dismissed';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const iosStandalone =
    'standalone' in window.navigator &&
    (window.navigator as { standalone?: boolean }).standalone === true;
  const displayStandalone = window.matchMedia('(display-mode: standalone)').matches;
  return iosStandalone || displayStandalone;
}

function isMobile(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  return /iPad|iPhone|iPod|Android/.test(ua);
}

function isIos(): boolean {
  if (typeof window === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(window.navigator.userAgent);
}

export function InstallPrompt() {
  const [show, setShow] = useState(false);
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    if (!isMobile() || isStandalone()) return;
    if (sessionStorage.getItem(DISMISS_KEY)) return;

    function onBeforeInstall(e: Event) {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      setShow(true);
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    const t = setTimeout(() => {
      if (isIos()) setShow(true);
    }, 1500);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      clearTimeout(t);
    };
  }, []);

  function dismiss() {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setShow(false);
  }

  async function install() {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    setShow(false);
  }

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 280, damping: 26 }}
          className="md:hidden fixed bottom-20 left-3 right-3 z-40 rounded-2xl bg-foreground text-background p-4 shadow-lift pb-safe"
        >
          <button
            onClick={dismiss}
            aria-label="Dispensar"
            className="absolute top-2 right-2 h-7 w-7 grid place-items-center text-background/70 hover:text-background"
          >
            <X className="h-4 w-4" />
          </button>
          <p className="eyebrow text-background/70">instale o app</p>
          {deferred ? (
            <>
              <p className="mt-1 text-sm leading-snug pr-6">
                Instale o Underline na sua tela inicial para acesso rápido e notificações.
              </p>
              <button
                onClick={install}
                className="mt-3 inline-flex items-center gap-2 rounded-xl bg-background text-foreground px-3 py-2 text-sm font-medium"
              >
                <Download className="h-4 w-4" />
                Instalar
              </button>
            </>
          ) : (
            <p className="mt-1 text-sm leading-snug pr-6">
              Toque em <Share className="inline h-3.5 w-3.5 mx-0.5" /> Compartilhar e escolha{' '}
              <b>Adicionar à Tela de Início</b>.
            </p>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
